import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import {
  ANTECEDENT_OPTIONS,
  BEHAVIOR_TYPE_OPTIONS,
  CONSEQUENCE_OPTIONS,
  FUNCTIONS_OF_BEHAVIOR,
  INTERVENTION_OPTIONS,
  LOCATION_OPTIONS,
  SETTING_ACTIVITIES,
  STUDENT_REACTION_OPTIONS,
} from '../../../lib/pbisConstants';
import {
  QUICK_BEHAVIOR_CHIPS,
  QUICK_ANTECEDENT_CHIPS,
  QUICK_CONSEQUENCE_CHIPS,
  QUICK_FUNCTION_CHIPS,
  describeBehavior,
} from '../../../lib/behaviorDescriptionTemplates';
import { useIEPGoals } from '../../../context/IEPGoalsContext';

// Reviewer-facing hint for each antecedent choice: the *likely* function
// suggests where to look first. This is a starting point for the
// hypothesis, not a definitive answer — the app makes that explicit.
const FUNCTION_HINTS_BY_ANTECEDENT = {
  'task-demand': 'escape',
  'denied-access': 'tangible',
  'attention-to-others': 'attention',
  alone: 'attention',
  'peer-conflict': 'attention',
  'sensory-overload': 'sensory',
  unstructured: 'sensory',
  'told-no': 'tangible',
  transition: 'escape',
  waiting: 'attention',
  'corrective-feedback': 'escape',
  'non-preferred-activity': 'escape',
  'schedule-change': 'escape',
};

// Every behavior incident has to be tied to a wall-clock time and a
// physical location — that's a state/federal FBA minimum. But the vast
// majority of the time the teacher is entering the record right after
// the incident happens in the classroom, so we default those fields
// aggressively and let the teacher override via "Show details".
const DEFAULT_LOCATION_IN_SIMPLE_MODE = 'classroom';

const initialForm = (studentId) => ({
  studentId: studentId || '',
  date: new Date().toISOString().split('T')[0],
  time: new Date().toTimeString().slice(0, 5),
  location: '',
  settingActivity: '',
  antecedent: '',
  antecedentNotes: '',
  behaviorType: '',
  behaviorDescription: '',
  intensity: 3,
  durationSeconds: null,
  consequence: '',
  studentReaction: '',
  hypothesizedFunction: '',
  interventionUsed: '',
  staffInvolved: [],
  followUpRequired: false,
  notes: '',
  // Optional list of behavior_goals.id values this incident is data
  // for. Feeds public.behavior_incidents.linked_goal_ids (see migration
  // 0011). Empty by default so a teacher who just wants to log the
  // incident isn't slowed down.
  linkedGoalIds: [],
});

const staffOptions = [
  { value: 'teacher', label: 'Classroom Teacher' },
  { value: 'special-ed-teacher', label: 'Special Ed Teacher' },
  { value: 'paraprofessional', label: 'Paraprofessional' },
  { value: 'behavior-specialist', label: 'Behavior Specialist' },
  { value: 'counselor', label: 'Counselor' },
  { value: 'administrator', label: 'Administrator' },
  { value: 'related-service', label: 'Related Service (SLP / OT / PT)' },
  { value: 'other', label: 'Other Staff' },
];

const functionOptions = FUNCTIONS_OF_BEHAVIOR.map((f) => ({
  value: f.value,
  label: f.label,
  description: f.description,
}));

// Tailwind class tokens for the colored chip buttons. Kept as static
// strings (not template literals) so the Tailwind JIT can see every
// class at build time.
const CHIP_COLOR_CLASSES = {
  amber:  { base: 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100',        active: 'border-amber-600  bg-amber-600  text-white shadow-md' },
  orange: { base: 'border-orange-300 bg-orange-50 text-orange-900 hover:bg-orange-100',    active: 'border-orange-600 bg-orange-600 text-white shadow-md' },
  red:    { base: 'border-red-300 bg-red-50 text-red-900 hover:bg-red-100',                active: 'border-red-600    bg-red-600    text-white shadow-md' },
  blue:   { base: 'border-blue-300 bg-blue-50 text-blue-900 hover:bg-blue-100',            active: 'border-blue-600   bg-blue-600   text-white shadow-md' },
  violet: { base: 'border-violet-300 bg-violet-50 text-violet-900 hover:bg-violet-100',    active: 'border-violet-600 bg-violet-600 text-white shadow-md' },
  cyan:   { base: 'border-cyan-300 bg-cyan-50 text-cyan-900 hover:bg-cyan-100',            active: 'border-cyan-600   bg-cyan-600   text-white shadow-md' },
  gray:   { base: 'border-gray-300 bg-gray-50 text-gray-900 hover:bg-gray-100',            active: 'border-gray-700   bg-gray-700   text-white shadow-md' },
};

// Neutral (no color hint) chip — used for antecedent + consequence, which
// don't have an obvious color mapping. Just big, tappable, high-contrast.
const NEUTRAL_CHIP_CLASSES = {
  base:   'border-2 border-gray-300 bg-white text-gray-900 hover:bg-gray-50',
  active: 'border-2 border-primary bg-primary text-primary-foreground shadow-md',
};

const chipClass = (color, isActive) => {
  const c = CHIP_COLOR_CLASSES[color] || CHIP_COLOR_CLASSES.gray;
  return `border-2 ${isActive ? c.active : c.base}`;
};

const IncidentEntryForm = ({ selectedStudent, onIncidentSubmit, isSubmitting }) => {
  const [formData, setFormData] = useState(() => initialForm(selectedStudent?.id));

  // The student's active IEP goals feed the optional "Does this relate
  // to an IEP goal?" picker at the bottom of the form. We only pull
  // goals for the currently-selected student, and we treat "active" as
  // anything not mastered or discontinued (in_progress and not_started
  // both count — a not-yet-started goal can still receive baseline
  // data). Goals sort alphabetically by their plain-English "specific"
  // field so the picker is stable across renders.
  const { goals: allGoals } = useIEPGoals();
  const relatedGoals = useMemo(() => {
    if (!selectedStudent?.id) return [];
    return (Array.isArray(allGoals) ? allGoals : [])
      .filter((g) => g.studentId === selectedStudent.id)
      .filter((g) => g.status !== 'mastered' && g.status !== 'discontinued')
      .sort((a, b) => (a.specific || '').localeCompare(b.specific || ''));
  }, [allGoals, selectedStudent?.id]);

  const toggleLinkedGoal = (goalId) => {
    if (!goalId) return;
    setFormData((prev) => {
      const current = Array.isArray(prev.linkedGoalIds) ? prev.linkedGoalIds : [];
      const next = current.includes(goalId)
        ? current.filter((id) => id !== goalId)
        : [...current, goalId];
      return { ...prev, linkedGoalIds: next };
    });
  };

  // Mode: 'simple' (default — big chips, description templates, most
  // fields hidden) vs 'detailed' (every FBA/BIP field visible on one
  // page, no toggles). The Simple mode is designed for a first-year
  // teacher or a paraprofessional documenting an incident on a phone;
  // the Detailed mode is for a behavior specialist doing formal FBA
  // data collection or writing a BIP.
  //
  // Persist the preference in localStorage so a BCBA who always wants
  // Detailed doesn't have to flip it every visit — but default new users
  // to Simple.
  const [mode, setMode] = useState(() => {
    try {
      const saved = typeof window !== 'undefined'
        ? window.localStorage.getItem('incidentFormMode')
        : null;
      return saved === 'detailed' ? 'detailed' : 'simple';
    } catch {
      return 'simple';
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem('incidentFormMode', mode);
    } catch {
      // localStorage may be blocked (private mode, embedded frames). Not fatal.
    }
  }, [mode]);

  // Progressive-disclosure toggles inside Simple mode.
  const [happenedJustNow, setHappenedJustNow]     = useState(true);
  const [showAntecedentDetail, setShowAntecedentDetail] = useState(false);
  const [showAdvanced, setShowAdvanced]           = useState(false);

  // Timer state
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [timerStart, setTimerStart]       = useState(null);
  const [elapsedMs, setElapsedMs]         = useState(0);

  // `missingFields` drives the big red banner + inline red rings that
  // appear when the user clicks "Save" without filling every required
  // field. We ship our own validator + `noValidate` on the <form>
  // because the browser's native "Please fill out this field" tooltip
  // pops up on whatever required field the browser sees first — which
  // is usually scrolled off-screen — so from the user's perspective
  // it looks like the button did nothing.
  const [missingFields, setMissingFields] = useState([]);
  const errorBannerRef = useRef(null);
  // Per-section refs so we can (1) scroll the first missing section into
  // view, and (2) paint a red outline around it. That way the teacher
  // isn't left hunting for what "C" means — they see the specific section
  // glowing red.
  const sectionRefs = {
    when:        useRef(null),
    where:       useRef(null),
    behavior:    useRef(null),
    antecedent:  useRef(null),
    consequence: useRef(null),
    function:    useRef(null),
  };

  useEffect(() => {
    // Reset linked-goal chips whenever the incident switches to a
    // different student — the previous student's goals don't apply.
    setFormData((prev) => ({
      ...prev,
      studentId: selectedStudent?.id || '',
      linkedGoalIds: [],
    }));
  }, [selectedStudent?.id]);

  // Live timer
  useEffect(() => {
    if (!isTimerActive || !timerStart) return undefined;
    const interval = setInterval(() => {
      setElapsedMs(Date.now() - timerStart);
    }, 1000);
    return () => clearInterval(interval);
  }, [isTimerActive, timerStart]);

  const studentFirstName = selectedStudent?.firstName
    || (selectedStudent?.name ? selectedStudent.name.split(' ')[0] : '')
    || 'The student';
  const studentFullName = selectedStudent?.firstName
    ? `${selectedStudent.firstName} ${selectedStudent.lastName ?? ''}`.trim()
    : selectedStudent?.name || 'the selected student';

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // When the antecedent changes and no function has been picked yet,
      // pre-fill the function hypothesis from the hint table. Reviewers
      // still want the user to confirm, so we don't overwrite an explicit
      // choice.
      if (field === 'antecedent' && !prev.hypothesizedFunction) {
        const hinted = FUNCTION_HINTS_BY_ANTECEDENT[value];
        if (hinted) next.hypothesizedFunction = hinted;
      }
      return next;
    });
  };

  // Behavior chip picker — sets `behaviorType` AND pre-fills the
  // operational description with the first template so the required
  // description field is never blank. The teacher can pick a different
  // template or edit freely.
  const handleQuickBehaviorSelect = (behaviorType) => {
    setFormData((prev) => {
      const templates = describeBehavior(behaviorType, studentFirstName);
      // Only overwrite the description if the user hasn't typed
      // something custom already, OR if the current text is a
      // template from a *different* behavior (i.e. they clicked a
      // different chip and want to see fresh templates).
      const prevTemplatesForCurrent = describeBehavior(prev.behaviorType, studentFirstName);
      const currentIsTemplate =
        !prev.behaviorDescription ||
        prevTemplatesForCurrent.includes(prev.behaviorDescription);
      const nextDescription = currentIsTemplate
        ? (templates[0] || '')
        : prev.behaviorDescription;
      return { ...prev, behaviorType, behaviorDescription: nextDescription };
    });
  };

  const applyTemplate = (text) => {
    setFormData((prev) => ({ ...prev, behaviorDescription: text }));
  };

  const startTimer = () => {
    setTimerStart(Date.now());
    setElapsedMs(0);
    setIsTimerActive(true);
  };

  const stopTimer = () => {
    setIsTimerActive(false);
    if (timerStart) {
      const seconds = Math.floor((Date.now() - timerStart) / 1000);
      setFormData((prev) => ({ ...prev, durationSeconds: seconds }));
    }
  };

  const resetTimer = () => {
    setIsTimerActive(false);
    setTimerStart(null);
    setElapsedMs(0);
    setFormData((prev) => ({ ...prev, durationSeconds: null }));
  };

  const formatMs = (ms) => {
    const total = Math.floor(ms / 1000);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const resetForm = () => {
    setFormData(initialForm(selectedStudent?.id));
    resetTimer();
    setHappenedJustNow(true);
    setShowAntecedentDetail(false);
    setShowAdvanced(false);
    setMissingFields([]);
  };

  const hypothesisPreview = useMemo(() => {
    // Build a plain-language "When X, student engages in Y to obtain/avoid Z"
    // preview. Reviewers grade BIPs partly on whether the FBA hypothesis is
    // clearly stated — showing this preview while the teacher enters data
    // reinforces the frame.
    const parts = [];
    if (formData.antecedent) {
      const label = ANTECEDENT_OPTIONS.find((a) => a.value === formData.antecedent)?.label;
      if (label) parts.push(`When ${label.toLowerCase()},`);
    }
    if (formData.behaviorType) {
      const label = BEHAVIOR_TYPE_OPTIONS.find((b) => b.value === formData.behaviorType)?.label;
      if (label) parts.push(`the student engages in ${label.toLowerCase()}`);
    }
    if (formData.hypothesizedFunction) {
      const fn = FUNCTIONS_OF_BEHAVIOR.find((f) => f.value === formData.hypothesizedFunction);
      if (fn) {
        const verb = fn.value === 'escape' ? 'to escape / avoid' : 'to obtain';
        parts.push(
          fn.value === 'unknown'
            ? '(function unclear — collect more data)'
            : `${verb} ${fn.label.toLowerCase().replace(' / avoid', '')}.`
        );
      }
    }
    return parts.join(' ');
  }, [formData.antecedent, formData.behaviorType, formData.hypothesizedFunction]);

  // Required-field spec used by BOTH the submit-time validator and the
  // inline red-ring highlighting. Keep the "friendly name" short — this
  // is what the teacher sees in the "You still need to fill in…" list.
  //
  // Note: these fields are all state/federal FBA/BIP minimums. Even in
  // Simple mode, the teacher still needs to answer A, B (type + short
  // description), C, and function. The Simple UI just makes those
  // answerable in taps rather than dropdowns.
  const REQUIRED_FIELDS = [
    { key: 'date',                 friendly: 'When it happened — date',                  section: 'when'        },
    { key: 'time',                 friendly: 'When it happened — time',                  section: 'when'        },
    { key: 'location',             friendly: 'Where it happened',                        section: 'where'       },
    { key: 'antecedent',           friendly: 'What happened right before',               section: 'antecedent'  },
    { key: 'behaviorType',         friendly: 'What they did',                            section: 'behavior'    },
    { key: 'behaviorDescription',  friendly: 'A short description of what they did',     section: 'behavior'    },
    { key: 'consequence',          friendly: 'What happened right after',                section: 'consequence' },
    { key: 'hypothesizedFunction', friendly: 'Best guess at why',                        section: 'function'    },
  ];

  const validate = () => {
    const missing = [];
    // Simple mode auto-fills date/time (to now) and location
    // (Classroom) at submit time. Those aren't user-visible unless the
    // teacher toggles them into view, so they never count as "missing"
    // from a Simple-mode user's perspective.
    const augmented = { ...formData };
    if (mode === 'simple') {
      if (happenedJustNow || !augmented.date) augmented.date = new Date().toISOString().split('T')[0];
      if (happenedJustNow || !augmented.time) augmented.time = new Date().toTimeString().slice(0, 5);
      if (!augmented.location) augmented.location = DEFAULT_LOCATION_IN_SIMPLE_MODE;
    }

    for (const f of REQUIRED_FIELDS) {
      const v = augmented[f.key];
      if (v === null || v === undefined || String(v).trim() === '') {
        missing.push(f);
      }
    }
    return { missing, augmented };
  };

  const handleSubmit = (e) => {
    e?.preventDefault();

    const { missing, augmented } = validate();
    if (missing.length > 0) {
      setMissingFields(missing);
      // Scroll to the FIRST missing section (not the banner) so the
      // teacher sees exactly which section is empty. The section itself
      // will paint a red outline (see `isSectionMissing` below).
      // Fall back to the banner ref if the section isn't known (shouldn't
      // happen for required fields, but defensively).
      setTimeout(() => {
        const first = missing[0];
        const sectionRef = first?.section && sectionRefs[first.section];
        const target = sectionRef?.current || errorBannerRef.current;
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 20);
      return;
    }

    setMissingFields([]);
    // Persist the auto-filled defaults back into form state so the
    // record we hand to the parent has real values, not blanks.
    if (mode === 'simple') {
      setFormData(augmented);
    }

    let occurredAt = null;
    try {
      occurredAt = new Date(`${augmented.date}T${augmented.time}:00`).toISOString();
    } catch {
      occurredAt = new Date().toISOString();
    }
    onIncidentSubmit({ ...augmented, occurredAt });
  };

  // Clear the "field is missing" indicator as soon as the user fills the
  // field, so red rings don't linger on fields they've already fixed.
  useEffect(() => {
    if (missingFields.length === 0) return;
    setMissingFields((prev) =>
      prev.filter((m) => {
        const v = formData[m.key];
        return v === null || v === undefined || String(v).trim() === '';
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.date,
    formData.time,
    formData.location,
    formData.antecedent,
    formData.behaviorType,
    formData.behaviorDescription,
    formData.consequence,
    formData.hypothesizedFunction,
  ]);

  // Convenience: templates for the currently-selected behavior type,
  // interpolated with the student's first name.
  const currentTemplates = useMemo(
    () => describeBehavior(formData.behaviorType, studentFirstName),
    [formData.behaviorType, studentFirstName],
  );

  // Does the given section still have any unfilled required field? Used
  // to paint a red outline on the section wrapper so the teacher can
  // see WHERE to look, not just that "something" is missing.
  const isSectionMissing = (section) =>
    missingFields.some((m) => m.section === section);

  const sectionOutline = (section) =>
    isSectionMissing(section)
      ? 'ring-2 ring-red-400 border-red-300'
      : '';

  // ------------------------------------------------------------------
  // Simple-mode UI helpers
  // ------------------------------------------------------------------

  const BehaviorChipGrid = () => (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
      {QUICK_BEHAVIOR_CHIPS.map((chip) => {
        const active = formData.behaviorType === chip.value;
        return (
          <button
            key={chip.value}
            type="button"
            onClick={() => handleQuickBehaviorSelect(chip.value)}
            className={`${chipClass(chip.color, active)} rounded-lg px-3 py-3 text-left flex items-start gap-2 min-h-16 transition-colors`}
          >
            <Icon name={chip.icon} size={18} className="mt-0.5 flex-shrink-0" />
            <span className="text-sm font-medium leading-snug">{chip.plainLabel}</span>
          </button>
        );
      })}
    </div>
  );

  const AntecedentChipGrid = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
      {QUICK_ANTECEDENT_CHIPS.map((chip) => {
        const active = formData.antecedent === chip.value;
        return (
          <button
            key={chip.value}
            type="button"
            onClick={() => handleChange('antecedent', chip.value)}
            className={`${active ? NEUTRAL_CHIP_CLASSES.active : NEUTRAL_CHIP_CLASSES.base} rounded-lg px-3 py-3 text-left min-h-14 text-sm font-medium leading-snug transition-colors`}
          >
            {chip.plainLabel}
          </button>
        );
      })}
    </div>
  );

  const ConsequenceChipGrid = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
      {QUICK_CONSEQUENCE_CHIPS.map((chip) => {
        const active = formData.consequence === chip.value;
        return (
          <button
            key={chip.value}
            type="button"
            onClick={() => handleChange('consequence', chip.value)}
            className={`${active ? NEUTRAL_CHIP_CLASSES.active : NEUTRAL_CHIP_CLASSES.base} rounded-lg px-3 py-3 text-left min-h-14 text-sm font-medium leading-snug transition-colors`}
          >
            {chip.plainLabel}
          </button>
        );
      })}
    </div>
  );

  const FunctionChipGrid = () => (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {QUICK_FUNCTION_CHIPS.map((chip) => {
        const active = formData.hypothesizedFunction === chip.value;
        return (
          <button
            key={chip.value}
            type="button"
            onClick={() => handleChange('hypothesizedFunction', chip.value)}
            className={`${chipClass(chip.color, active)} rounded-lg px-3 py-3 text-left flex items-start gap-2 min-h-16 transition-colors`}
          >
            <Icon name={chip.icon} size={18} className="mt-0.5 flex-shrink-0" />
            <span className="text-sm font-medium leading-snug">{chip.plainLabel}</span>
          </button>
        );
      })}
    </div>
  );

  // Advanced fields shown either always in Detailed mode, or behind a
  // "Show more details" toggle in Simple mode.
  const AdvancedFields = () => (
    <div className="space-y-4 p-4 rounded-lg border border-dashed border-border bg-muted/20">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label="Where did it happen?"
          placeholder="Location"
          options={LOCATION_OPTIONS}
          value={formData.location}
          onChange={(v) => handleChange('location', v)}
        />
        <Select
          label="What was going on?"
          placeholder="Setting / activity"
          options={SETTING_ACTIVITIES}
          value={formData.settingActivity}
          onChange={(v) => handleChange('settingActivity', v)}
          searchable
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          More detail about what came before (optional)
        </label>
        <textarea
          value={formData.antecedentNotes}
          onChange={(e) => handleChange('antecedentNotes', e?.target?.value)}
          placeholder="Anything else that was happening — peers present, adult direction, environmental factors…"
          rows={2}
          className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            How intense? — {formData.intensity} / 5
          </label>
          <input
            type="range"
            min="1"
            max="5"
            value={formData.intensity}
            onChange={(e) => handleChange('intensity', parseInt(e?.target?.value, 10))}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>1 · Mild</span>
            <span>3 · Moderate</span>
            <span>5 · Severe</span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            How long did it last?
          </label>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant={isTimerActive ? 'destructive' : 'default'}
              onClick={isTimerActive ? stopTimer : startTimer}
            >
              <Icon name={isTimerActive ? 'Square' : 'Play'} size={14} />
              {isTimerActive ? 'Stop' : 'Start'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={resetTimer}
              disabled={!timerStart && !formData.durationSeconds}
            >
              <Icon name="RotateCcw" size={14} />
              Reset
            </Button>
            <span className="font-mono text-lg">
              {isTimerActive
                ? formatMs(elapsedMs)
                : formData.durationSeconds != null
                  ? formatMs(formData.durationSeconds * 1000)
                  : '0:00'}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label="What did the behavior do next?"
          placeholder="Result"
          options={STUDENT_REACTION_OPTIONS}
          value={formData.studentReaction}
          onChange={(v) => handleChange('studentReaction', v)}
        />
        <Select
          label="Which strategy did you try?"
          placeholder="Intervention used"
          options={INTERVENTION_OPTIONS}
          value={formData.interventionUsed}
          onChange={(v) => handleChange('interventionUsed', v)}
          searchable
        />
      </div>

      <Select
        label="Who was there?"
        placeholder="Staff involved"
        options={staffOptions}
        value={formData.staffInvolved}
        onChange={(v) => handleChange('staffInvolved', v)}
        multiple
      />

      <Checkbox
        label="Someone needs to follow up on this"
        description="Check this if the team should meet, contact family, or review the BIP because of this incident."
        checked={formData.followUpRequired}
        onChange={(e) => handleChange('followUpRequired', e?.target?.checked)}
      />

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Anything else worth noting?</label>
        <textarea
          value={formData.notes}
          onChange={(e) => handleChange('notes', e?.target?.value)}
          placeholder="Medical / sensory factors, communication attempts, anything the team should know…"
          rows={3}
          className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
        />
      </div>
    </div>
  );

  const isSimple = mode === 'simple';

  return (
    <div className="bg-card rounded-lg border border-border p-4 sm:p-6">
      <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-card-foreground">Log an incident</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {isSimple ? (
              <>
                Tap what happened for <span className="font-medium">{studentFullName}</span>.
                It should only take about a minute.
              </>
            ) : (
              <>
                Detailed FBA/BIP entry for <span className="font-medium">{studentFullName}</span>.
                Fill in A, B, C, and function — plus intensity, duration, and follow-up.
              </>
            )}
          </p>
        </div>

        <div className="flex flex-col items-start sm:items-end gap-2">
          {/* Simple / Detailed toggle — a familiar pill switcher. Defaults
              to Simple so a first-year teacher lands in the easy path.
              A behavior specialist / case manager who wants every field
              visible flips it once and their preference sticks for the
              session. */}
          <div
            role="tablist"
            aria-label="Form mode"
            className="inline-flex rounded-full border border-border bg-muted/40 p-1 text-sm"
          >
            <button
              type="button"
              role="tab"
              aria-selected={isSimple}
              onClick={() => setMode('simple')}
              className={`px-3 py-1.5 rounded-full font-medium transition-colors ${
                isSimple ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground'
              }`}
            >
              Simple
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={!isSimple}
              onClick={() => setMode('detailed')}
              className={`px-3 py-1.5 rounded-full font-medium transition-colors ${
                !isSimple ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground'
              }`}
            >
              Detailed
            </button>
          </div>
          <Button variant="outline" onClick={resetForm} disabled={isSubmitting}>
            <Icon name="RotateCcw" size={16} />
            Reset
          </Button>
        </div>
      </div>

      {/* noValidate: we ship our own validator (see handleSubmit + the
          banner below). Without it, the browser fires its native
          "Please fill out this field" tooltip on the first empty
          required field — usually scrolled up out of view — and the
          user thinks the Save button did nothing. */}
      <form onSubmit={handleSubmit} className="space-y-6" noValidate>
        {missingFields.length > 0 && (
          <div
            ref={errorBannerRef}
            role="alert"
            className="rounded-lg border-2 border-red-300 bg-red-50 p-4"
          >
            <div className="flex items-start gap-2">
              <Icon
                name="AlertCircle"
                size={20}
                className="mt-0.5 flex-shrink-0 text-red-600"
              />
              <div className="flex-1">
                <p className="text-base font-semibold text-red-900">
                  You still need to fill in {missingFields.length}{' '}
                  {missingFields.length === 1 ? 'thing' : 'things'} before you can save.
                </p>
                <ul className="mt-2 list-disc pl-5 text-sm text-red-800">
                  {missingFields.map((m) => (
                    <li key={m.key}>{m.friendly}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {isSimple ? (
          // -------------------------- SIMPLE MODE --------------------------
          <>
            {/* When ------------------------------------------------------ */}
            <section
              ref={sectionRefs.when}
              className={`p-4 rounded-lg border border-border bg-muted/20 space-y-3 ${sectionOutline('when')}`}
            >
              <Checkbox
                label="This happened just now"
                description="If you're logging something from earlier today or another day, uncheck this and pick a date & time."
                checked={happenedJustNow}
                onChange={(e) => {
                  setHappenedJustNow(e?.target?.checked);
                  if (e?.target?.checked) {
                    setFormData((prev) => ({
                      ...prev,
                      date: new Date().toISOString().split('T')[0],
                      time: new Date().toTimeString().slice(0, 5),
                    }));
                  }
                }}
              />
              {!happenedJustNow && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleChange('date', e?.target?.value)}
                  />
                  <Input
                    label="Time"
                    type="time"
                    value={formData.time}
                    onChange={(e) => handleChange('time', e?.target?.value)}
                  />
                </div>
              )}
            </section>

            {/* B — Behavior --------------------------------------------- */}
            <section
              ref={sectionRefs.behavior}
              className={`p-4 rounded-lg border border-border bg-muted/30 space-y-4 ${sectionOutline('behavior')}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-base font-bold">
                  1
                </span>
                <h3 className="font-semibold text-card-foreground text-lg">What did they do?</h3>
                <span className="text-sm text-muted-foreground">Tap the closest match.</span>
                {isSectionMissing('behavior') && (
                  <span className="ml-auto text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5">
                    Not filled in yet
                  </span>
                )}
              </div>
              <BehaviorChipGrid />

              {formData.behaviorType && currentTemplates.length > 0 && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-foreground">
                    Pick a description that fits — or edit it below.
                  </label>
                  <div className="grid grid-cols-1 gap-2">
                    {currentTemplates.map((tpl, i) => {
                      const active = formData.behaviorDescription === tpl;
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => applyTemplate(tpl)}
                          className={`text-left rounded-md border-2 px-3 py-2 text-sm leading-snug transition-colors ${
                            active
                              ? 'border-primary bg-primary/10 text-foreground'
                              : 'border-gray-200 bg-white text-gray-800 hover:bg-gray-50'
                          }`}
                        >
                          <span className="font-mono text-xs text-muted-foreground mr-2">
                            {i + 1}.
                          </span>
                          {tpl}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Short description <span className="text-destructive">*</span>
                </label>
                <textarea
                  value={formData.behaviorDescription}
                  onChange={(e) => handleChange('behaviorDescription', e?.target?.value)}
                  placeholder='Example: "Sam threw his iPad three out of five times when asked to do math."'
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Write what you saw or could count — not how it felt. That's what makes it useful for the team.
                </p>
              </div>
            </section>

            {/* A — Antecedent ------------------------------------------ */}
            <section
              ref={sectionRefs.antecedent}
              className={`p-4 rounded-lg border border-border bg-muted/30 space-y-4 ${sectionOutline('antecedent')}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-base font-bold">
                  2
                </span>
                <h3 className="font-semibold text-card-foreground text-lg">What happened right before?</h3>
                <span className="text-sm text-muted-foreground">Best guess is fine.</span>
                {isSectionMissing('antecedent') && (
                  <span className="ml-auto text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5">
                    Not filled in yet
                  </span>
                )}
              </div>
              <AntecedentChipGrid />

              <button
                type="button"
                onClick={() => setShowAntecedentDetail((v) => !v)}
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                <Icon name={showAntecedentDetail ? 'ChevronUp' : 'ChevronDown'} size={14} />
                {showAntecedentDetail ? 'Hide extra detail' : 'Add more detail (optional)'}
              </button>
              {showAntecedentDetail && (
                <textarea
                  value={formData.antecedentNotes}
                  onChange={(e) => handleChange('antecedentNotes', e?.target?.value)}
                  placeholder="Anything else about what was happening right before — peers, adult direction, environment…"
                  rows={2}
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              )}
            </section>

            {/* C — Consequence ---------------------------------------- */}
            <section
              ref={sectionRefs.consequence}
              className={`p-4 rounded-lg border border-border bg-muted/30 space-y-4 ${sectionOutline('consequence')}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-base font-bold">
                  3
                </span>
                <h3 className="font-semibold text-card-foreground text-lg">What happened right after?</h3>
                <span className="text-sm text-muted-foreground">What did you or another staff member do?</span>
                {isSectionMissing('consequence') && (
                  <span className="ml-auto text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5">
                    Not filled in yet
                  </span>
                )}
              </div>
              <ConsequenceChipGrid />
            </section>

            {/* Function ---------------------------------------------- */}
            <section
              ref={sectionRefs.function}
              className={`p-4 rounded-lg border border-primary/30 bg-primary/5 space-y-4 ${sectionOutline('function')}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-base font-bold">
                  4
                </span>
                <Icon name="Brain" size={20} className="text-primary" />
                <h3 className="font-semibold text-card-foreground text-lg">Why do you think it happened?</h3>
                {isSectionMissing('function') && (
                  <span className="ml-auto text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5">
                    Not filled in yet
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                Just your best guess for now — the app will suggest one based on the trigger you picked. You can change it.
              </p>
              <FunctionChipGrid />
              {hypothesisPreview && (
                <div className="p-3 rounded-md bg-white/70 border border-border text-sm text-card-foreground">
                  <span className="font-medium">In one sentence: </span>
                  {hypothesisPreview}
                </div>
              )}
            </section>

            {/* Show more details -------------------------------------- */}
            <div>
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-border bg-muted/20 hover:bg-muted/40 text-foreground transition-colors"
                aria-expanded={showAdvanced}
              >
                <Icon name={showAdvanced ? 'ChevronUp' : 'Plus'} size={16} />
                <span className="font-medium">
                  {showAdvanced
                    ? 'Hide extra details'
                    : 'Show more details (intensity, duration, location, staff, follow-up, notes)'}
                </span>
              </button>
              {showAdvanced && (
                <div className="mt-3">
                  <AdvancedFields />
                </div>
              )}
            </div>
          </>
        ) : (
          // -------------------------- DETAILED MODE --------------------------
          <>
            {/* When + where ------------------------------------------------ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Date"
                type="date"
                value={formData.date}
                onChange={(e) => handleChange('date', e?.target?.value)}
                required
              />
              <Input
                label="Time"
                type="time"
                value={formData.time}
                onChange={(e) => handleChange('time', e?.target?.value)}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Location"
                placeholder="Where did it happen?"
                options={LOCATION_OPTIONS}
                value={formData.location}
                onChange={(v) => handleChange('location', v)}
                required
              />
              <Select
                label="Setting / activity"
                placeholder="What was going on?"
                options={SETTING_ACTIVITIES}
                value={formData.settingActivity}
                onChange={(v) => handleChange('settingActivity', v)}
                searchable
              />
            </div>

            {/* A — Antecedent -------------------------------------------- */}
            <section className="p-4 rounded-lg border border-border bg-muted/30 space-y-4">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary text-primary-foreground text-sm font-bold">
                  A
                </span>
                <h3 className="font-semibold text-card-foreground">Antecedent</h3>
                <span className="text-xs text-muted-foreground">What happened right before?</span>
              </div>
              <Select
                label="Antecedent"
                placeholder="Pick the most likely trigger"
                options={ANTECEDENT_OPTIONS}
                value={formData.antecedent}
                onChange={(v) => handleChange('antecedent', v)}
                searchable
                required
              />
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Additional antecedent detail (optional)
                </label>
                <textarea
                  value={formData.antecedentNotes}
                  onChange={(e) => handleChange('antecedentNotes', e?.target?.value)}
                  placeholder="Anything else about what was happening right before…"
                  rows={2}
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>
            </section>

            {/* B — Behavior --------------------------------------------- */}
            <section className="p-4 rounded-lg border border-border bg-muted/30 space-y-4">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary text-primary-foreground text-sm font-bold">
                  B
                </span>
                <h3 className="font-semibold text-card-foreground">Behavior</h3>
                <span className="text-xs text-muted-foreground">Observable & measurable — what did they do?</span>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Quick select</label>
                <BehaviorChipGrid />
              </div>

              <Select
                label="Behavior type"
                placeholder="Select primary behavior category"
                options={BEHAVIOR_TYPE_OPTIONS}
                value={formData.behaviorType}
                onChange={(v) => handleChange('behaviorType', v)}
                searchable
                required
              />

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Operational description <span className="text-destructive">*</span>
                </label>
                <textarea
                  value={formData.behaviorDescription}
                  onChange={(e) => handleChange('behaviorDescription', e?.target?.value)}
                  placeholder='Describe what you could see or measure. Example: "Student left seat, walked to the door, and hit the wall three times with an open hand."'
                  rows={3}
                  required
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Write what you observed, not how it felt — an operational (observable + measurable) definition is the FBA/BIP standard.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Intensity: {formData.intensity} / 5
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={formData.intensity}
                    onChange={(e) => handleChange('intensity', parseInt(e?.target?.value, 10))}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>1 · Mild</span>
                    <span>3 · Moderate</span>
                    <span>5 · Severe</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Duration</label>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={isTimerActive ? 'destructive' : 'default'}
                      onClick={isTimerActive ? stopTimer : startTimer}
                    >
                      <Icon name={isTimerActive ? 'Square' : 'Play'} size={14} />
                      {isTimerActive ? 'Stop' : 'Start'}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={resetTimer}
                      disabled={!timerStart && !formData.durationSeconds}
                    >
                      <Icon name="RotateCcw" size={14} />
                      Reset
                    </Button>
                    <span className="font-mono text-lg">
                      {isTimerActive
                        ? formatMs(elapsedMs)
                        : formData.durationSeconds != null
                          ? formatMs(formData.durationSeconds * 1000)
                          : '0:00'}
                    </span>
                  </div>
                </div>
              </div>
            </section>

            {/* C — Consequence ------------------------------------------ */}
            <section className="p-4 rounded-lg border border-border bg-muted/30 space-y-4">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary text-primary-foreground text-sm font-bold">
                  C
                </span>
                <h3 className="font-semibold text-card-foreground">Consequence</h3>
                <span className="text-xs text-muted-foreground">What happened right after?</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Staff / peer response"
                  placeholder="Last thing done before the behavior stopped"
                  options={CONSEQUENCE_OPTIONS}
                  value={formData.consequence}
                  onChange={(v) => handleChange('consequence', v)}
                  searchable
                  required
                />
                <Select
                  label="Result"
                  placeholder="What did the behavior do next?"
                  options={STUDENT_REACTION_OPTIONS}
                  value={formData.studentReaction}
                  onChange={(v) => handleChange('studentReaction', v)}
                />
              </div>
              <Select
                label="Intervention used"
                placeholder="Which strategy was tried?"
                options={INTERVENTION_OPTIONS}
                value={formData.interventionUsed}
                onChange={(v) => handleChange('interventionUsed', v)}
                searchable
              />
            </section>

            {/* Function ------------------------------------------------- */}
            <section className="p-4 rounded-lg border border-primary/30 bg-primary/5 space-y-4">
              <div className="flex items-center gap-2">
                <Icon name="Brain" size={18} className="text-primary" />
                <h3 className="font-semibold text-card-foreground">Hypothesized function</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Based on A → B → C, what was the student trying to get or avoid? This is a working hypothesis — not a diagnosis.
              </p>
              <Select
                label="Function"
                placeholder="Pick the most likely function"
                options={functionOptions}
                value={formData.hypothesizedFunction}
                onChange={(v) => handleChange('hypothesizedFunction', v)}
                required
              />
              {hypothesisPreview && (
                <div className="p-3 rounded-md bg-white/70 border border-border text-sm text-card-foreground">
                  <span className="font-medium">Working hypothesis: </span>
                  {hypothesisPreview}
                </div>
              )}
            </section>

            {/* Follow-up + staff --------------------------------------- */}
            <Select
              label="Staff involved"
              placeholder="Who was present?"
              options={staffOptions}
              value={formData.staffInvolved}
              onChange={(v) => handleChange('staffInvolved', v)}
              multiple
            />

            <Checkbox
              label="Follow-up required"
              description="Check if this incident triggers a team meeting, parent contact, or BSP review."
              checked={formData.followUpRequired}
              onChange={(e) => handleChange('followUpRequired', e?.target?.checked)}
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Additional notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e?.target?.value)}
                placeholder="Context, medical/sensory factors, communication attempts, anything else…"
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              />
            </div>
          </>
        )}

        {/* Related IEP goals — optional linkage. Shown in BOTH Simple
            and Detailed modes because a para logging a "refused task"
            incident should be able to tag it against the student's
            social-emotional / behavioral goal in one tap, no matter
            which mode they're in. Only rendered when the selected
            student actually has at least one active goal — otherwise
            the section would be an empty prompt with no answer to it.
            */}
        {selectedStudent?.id && relatedGoals.length > 0 && (
          <section className="p-4 rounded-lg border-2 border-dashed border-indigo-300 bg-indigo-50/50 space-y-3">
            <div className="flex items-start gap-2">
              <Icon name="Link" size={18} className="text-indigo-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-card-foreground">
                  Does this relate to an IEP goal? <span className="font-normal text-muted-foreground">(optional)</span>
                </h3>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Tap any goal this incident is data for. It will show
                  up on that goal's page under "Recent behavior
                  incidents" — no need to log the same thing twice.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
              {relatedGoals.map((goal) => {
                const isLinked = (formData.linkedGoalIds || []).includes(goal.id);
                const summary =
                  (goal.specific || '').trim() ||
                  (goal.measurable || '').trim() ||
                  'Untitled goal';
                const areaLabel = (goal.goalArea || 'other').replace(/-/g, ' / ');
                return (
                  <button
                    key={goal.id}
                    type="button"
                    onClick={() => toggleLinkedGoal(goal.id)}
                    aria-pressed={isLinked}
                    className={`text-left rounded-lg border-2 px-3 py-3 transition-colors ${
                      isLinked
                        ? 'border-indigo-600 bg-indigo-600 text-white shadow-md'
                        : 'border-indigo-300 bg-white text-gray-900 hover:bg-indigo-100/40'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <Icon
                        name={isLinked ? 'CheckSquare' : 'Square'}
                        size={16}
                        className={`mt-0.5 flex-shrink-0 ${
                          isLinked ? 'text-white' : 'text-indigo-500'
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-xs font-medium uppercase tracking-wide ${
                            isLinked ? 'text-white/85' : 'text-indigo-600'
                          }`}
                        >
                          {areaLabel}
                        </p>
                        <p className="text-sm leading-snug mt-0.5 line-clamp-2">
                          {summary}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {selectedStudent?.id && relatedGoals.length === 0 && (
          <div className="p-3 rounded-md bg-muted/40 border border-dashed border-border text-sm text-muted-foreground">
            <Icon name="Info" size={14} className="inline mr-1.5 -mt-0.5" />
            {`${studentFirstName} doesn't have any active IEP goals yet — this incident won't be linked to a goal. `}
            <span className="italic">Add a goal on the IEP Goals page to enable linking on future incidents.</span>
          </div>
        )}

        {/* Save / cancel row. Full-width on mobile, right-aligned on
            desktop — this is the most important tap on the page. */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t border-border">
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={resetForm}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="lg"
            loading={isSubmitting}
            iconName="Check"
            iconPosition="left"
            className="min-w-40 text-base"
          >
            {isSubmitting ? 'Saving…' : 'Save incident'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default IncidentEntryForm;
